/** One line row's frame (moved from `builder/rows.tsx` at step 8). */
import { type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { InternalBand, lineGrid, useLineGrid } from './grid.js'
import { RowMenuButton, type MenuAt } from './add-row.js'

// ---------------------------------------------------------------------------
// The line
// ---------------------------------------------------------------------------

export interface LineShellProps {
  /** The 24px kind tile. */
  tile: ReactNode
  name: string
  onOpen?: () => void
  tags?: ReactNode
  sub?: string
  /** A number box, or — where the grid's quantity is a rule — the rule cell. */
  qty: ReactNode
  unit?: ReactNode
  /** Price each. On the quote grid it folds under the name when the card is narrow. */
  price: ReactNode
  /** What Price each folds into on a narrow card ("× $55.11 each"). */
  priceFolded?: ReactNode
  /** Ours, on the band: the whole line's cost. */
  cost?: ReactNode
  /** Ours, on the band: the line's margin. */
  margin?: ReactNode
  /** Price — the customer's number. */
  total: ReactNode
  onMenu?: (at: MenuAt) => void
  /** Carried as `data-line-id`, so a check can find the row it is about. */
  lineId?: string
}

export function LineShell({
  tile, name, onOpen, tags, sub, qty, unit, price, priceFolded, cost, margin, total, onMenu,
  lineId,
}: LineShellProps) {
  const { mode, qty: qtyMode } = useLineGrid()
  const folded = mode === 'compact'
  const second = folded ? priceFolded : sub

  return (
    <div
      data-line-id={lineId}
      className={cn(lineGrid(mode, qtyMode),
        /* One row shape, whichever tree this is drawn in. */
        'border-t border-line-soft py-[7px] pl-[66px] pr-3.5 text-[13px] hover:bg-hover')}
    >
      {tile}

      {/*
        * The name opens the editor; the row does not. A row-wide click target
        * that contains a text input is a click target that swallows the click
        * somebody aimed at the input.
        */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          {onOpen
            ? (
              <button
                type="button"
                data-line-name=""
                onClick={onOpen}
                className="truncate text-left font-semibold text-text hover:text-accent"
              >
                {name}
              </button>
            )
            : <b className="truncate font-semibold text-text">{name}</b>}
          {tags}
        </span>
        {second && (
          <span className={cn('tnum truncate text-[11.5px]',
            folded ? 'text-text-muted' : 'text-text-faint')}>{second}</span>
        )}
      </div>

      {qty}
      {unit ?? <span />}
      {!folded && price}
      {total}
      <InternalBand inset="-my-[7px]" cost={cost ?? <span />} margin={margin} />
      {onMenu
        ? <RowMenuButton label={`${name} options`} onOpen={onMenu} small />
        : <span />}
    </div>
  )
}
